import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { ifc_api } from "@/apps/riyasol/lib/envars.conf"
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item"
import { Spinner } from "@/components/ui/shadcn-io/spinner"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  DoorOpen,
  Layers2,
  MapPin,
  Settings2,
  VectorSquare,
} from "lucide-react"
import { Identifier, useDataProvider, useRedirect } from "ra-core"
import { useState } from "react"

// TODO replace with dynamic data

const phases = [
  { id: 1, name: "Projet enregistré" },
  { id: 2, name: "Phase de conception" },
  { id: 3, name: "Permis et autorisations" },
  { id: 4, name: "Préparation du chantier" },
  { id: 5, name: "Fondations" },
  { id: 6, name: "Structure et charpente" },
  { id: 7, name: "Toiture et extérieur" },
  { id: 8, name: "Installation électrique et plomberie" },
  { id: 9, name: "Finitions intérieures" },
  { id: 10, name: "Inspection et conformité" },
  { id: 11, name: "Réserves et corrections finales" },
  { id: 12, name: "Projet livré" },
]

const ProjectHeader = ({
  id,
  mode,
  showProgress,
  title,
  status,
  surface,
  city,
  region,
  floors,
  bedrooms,
}: {
  id: Identifier
  mode?: "edit" | "show"
  showProgress?: boolean
  title: string
  status: number
  surface: number
  city: string
  region: string
  floors: number
  bedrooms: number
}) => {
  const redirect = useRedirect()
  const dataProvider = useDataProvider()
  const [loading, setLoading] = useState(false)

  const generateIFCFile = async () => {
    setLoading(true)
    try {
      // Fetch materials using data provider
      const { data: rooms } = await dataProvider.getList("rooms", {
        filter: { project_id: id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
      })

      // Extract all material IDs from all rooms
      const materialIds = rooms
        .flatMap(room => room.material_ids || [])
        .map(material => material.mid)
        .filter(Boolean)

      const response = await fetch(ifc_api, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,
          name: title,
          material_ids: materialIds,
        }),
      })
      const data = await response.text()

      // Create a blob and download the file
      const blob = new Blob([data], { type: "text/plain" })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${title}.ifc`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error fetching IFC data:", error)
    }
    setLoading(false)
  }
  return (
    <>
      {showProgress && (
        <Badge
          variant="secondary"
          className="w-full border-2 border-secondary rounded-md p-1 px-2"
        >
          <ol className="flex items-center w-full">
            {phases.map(phase => (
              <li
                key={phase.id}
                className={`flex items-center after:w-full
                  ${
                    phase.id === status
                      ? " after:h-0.5 after:border-b after:border-1 w-full after:content-[''] after:border-primary-foreground  after:inline-block "
                      : phase.id < status
                        ? " after:h-0.5 after:border-b after:border-1 w-full after:border-primary"
                        : phase.id === phases.length
                          ? "after:border-primary-foreground after:inline-block"
                          : " after:h-0.5 after:border-b after:border-1 w-full after:border-primary-foreground after:inline-block"
                  } `}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={`cursor-pointer flex items-center justify-center ${phase.id === status ? "w-3 h-3 bg-primary-foreground border-primary" : phase.id < status ? "w-2 h-2 bg-primary" : "w-2 h-2 bg-primary-foreground"} shrink-0 rounded-full`}
                    >
                      {phase.id === status && <Spinner variant="ring" />}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    className={
                      phase.id > status
                        ? "bg-neutral-200 text-neutral-950 [&_svg]:bg-neutral-200 [&_svg]:fill-neutral-200"
                        : ""
                    }
                  >
                    <p>{phase.name}</p>
                  </TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ol>
        </Badge>
      )}
      <Item variant="muted" className="my-1 bg-secondary">
        <ItemMedia className="hidden md:flex">
          <Avatar className="w-18 h-18 rounded-sm">
            <AvatarImage
              src="https://plus.unsplash.com/premium_vector-1726932331958-ad37f87aaa9b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDN8fHxlbnwwfHx8fHw%3D"
              alt="Profile"
            />{" "}
            <AvatarFallback>{title}</AvatarFallback>
          </Avatar>
        </ItemMedia>
        <ItemContent className="gap-1">
          <ItemTitle>{title}</ItemTitle>
          <ItemDescription className="flex items-center gap-1 text-xs mb-2">
            <MapPin className="size-3" />
            {`${region}, ${city.toUpperCase()}`}
          </ItemDescription>
          <ItemDescription className="flex items-center gap-1 text-xs">
            <li className="flex items-center gap-1 mr-2">
              <VectorSquare className="size-4" />
              {surface}
              <sup>m2</sup>
            </li>
            <li className="flex items-center gap-1">
              <Layers2 className="size-4" />
              {floors}
            </li>
            <li className="flex items-center gap-1">
              <DoorOpen className="size-4" />
              {bedrooms}
            </li>
            {/* <div className="flex items-center gap-1">
              <Bath className="size-4" />
              {bathrooms}
            </div>
            <div className="flex items-center gap-1">
              <Toilet className="size-4" />
              {wcs}
            </div> */}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Badge
            variant="outline"
            className="hidden md:flex rounded-full border-2 border-primary-foreground"
          >
            <span
              className="size-1.5 rounded-full bg-primary"
              aria-hidden="true"
            />
            {phases[status - 1].name}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                type="button"
                size="icon"
                disabled={loading}
                className="rounded-full cursor-pointer "
              >
                {loading ? (
                  <Spinner className="size-5" variant="circle" />
                ) : (
                  <Settings2 className="size-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-66">
              <DropdownMenuGroup>
                {mode !== "edit" && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        redirect(`/projects/${id}/edit`)
                      }}
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    localStorage.setItem("pid", id.toString())
                    redirect("/storeys/create")
                  }}
                >
                  Add a Floor
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    localStorage.setItem("pid", id.toString())
                    redirect("/rooms/create")
                  }}
                >
                  Add a Room
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* <a href={`/${title}.ifc`} download> */}
                <DropdownMenuItem onClick={generateIFCFile}>
                  Download IFC file
                  <DropdownMenuShortcut>⌘ + I</DropdownMenuShortcut>
                </DropdownMenuItem>
                {/* </a> */}
                <DropdownMenuItem>
                  Export to PDF
                  <DropdownMenuShortcut>⌘ + P</DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </ItemActions>
      </Item>
    </>
  )
}

export default ProjectHeader
